// SPDX-FileCopyrightText: 2016-2026 Lari Natri <lari.natri@iki.fi>
// SPDX-License-Identifier: GPL-3.0-or-later

/** Parent-owned anchored key controls, displayed-state feedback, and native selection dialog. */

import {
  PDFJS_VIEWER_UI_HOOKS,
  type PdfjsViewer,
  type PdfjsViewerPageOverlayGeometry,
} from "@unilarva/pdfjs-viewer";
import type { Translator } from "@unilarva/translator";
import type { MultikeyController, MultikeyOccurrence } from "./multikey";
import { Logger } from "../../shared/src/generic/logger";

const MIN_MULTIKEY_BUTTON_SCALE: number = 0.2;

const mounts = new WeakMap<HTMLElement, () => void>();

/** Display completion is distinct from the controller's authoritative selection mapping. */
export interface MultikeyDisplayState {
  pending(occurrence?: MultikeyOccurrence): boolean;
  failed(occurrence?: MultikeyOccurrence): boolean;
  subscribe(listener: () => void): () => void;
}

/** Mounts app-owned, document-scoped transposition buttons using only public overlays. */
export async function mountMultikeyControls({
  viewer,
  controller,
  display,
  translator,
  root,
  signal,
  songTitle,
}: {
  viewer: Pick<PdfjsViewer, "documentId" | "registerPageOverlay" | "state">;
  controller: Pick<
    MultikeyController,
    "occurrences" | "offset" | "select" | "resetAll" | "subscribe" | "pending" | "ready"
  >;
  display: MultikeyDisplayState;
  translator: Pick<Translator, "translateKey" | "subscribe">;
  root: HTMLElement;
  signal: AbortSignal;
  songTitle?: (occurrence: MultikeyOccurrence) => Promise<string>;
}): Promise<() => void> {
  mounts.get(root)?.();
  const documentId = viewer.documentId;
  const cleanups: (() => void)[] = [];
  let disposed = false;
  const menu = root.querySelector<HTMLButtonElement>(".sb-pdf-reset-transpositions-btn");
  const dispose = () => {
    if (disposed) return;
    disposed = true;
    for (const cleanup of cleanups.splice(0).reverse()) cleanup();
    if (menu) {
      menu.hidden = true;
      menu.disabled = true;
      menu.setAttribute("aria-disabled", "true");
    }
    if (mounts.get(root) === dispose) mounts.delete(root);
  };
  mounts.set(root, dispose);
  signal.addEventListener("abort", dispose, { once: true });
  cleanups.push(() => signal.removeEventListener("abort", dispose));
  if (signal.aborted || documentId === null) {
    dispose();
    return dispose;
  }

  const document = root.ownerDocument;
  const window = document.defaultView;
  const dialog = document.createElement("dialog");
  dialog.className = "sb-transpose-dialog";
  const heading = document.createElement("h2");
  const header = document.createElement("header");
  const layout = document.createElement("div");
  layout.className = "sb-close-layout";
  const content = document.createElement("div");
  content.className = "sb-close-content";
  const closeRail = document.createElement("div");
  closeRail.className = "sb-close-rail";
  const close = document.createElement("button");
  close.type = "button";
  close.className = "sb-btn sb-btn--surface sb-btn--icon";
  const closeIcon = document.createElement("i");
  closeIcon.className = "sbi-x";
  closeIcon.setAttribute("aria-hidden", "true");
  close.append(closeIcon);
  header.append(heading);
  closeRail.append(close);
  const keys = document.createElement("div");
  keys.className = "sb-transpose-keys";
  content.append(header, keys);
  layout.append(closeRail, content);
  dialog.append(layout);
  // Fullscreen and document teardown share the viewer root's lifetime.
  root.append(dialog);
  let active:
    { record: MultikeyOccurrence; button: HTMLButtonElement; offset: () => number } | undefined;
  const closeDialog = () => {
    if (!dialog.open) return;
    dialog.close();
    active?.button.setAttribute("aria-expanded", "false");
    if (!disposed && active?.button.isConnected) active.button.focus({ preventScroll: true });
    active = undefined;
  };
  // Public overlays isolate keyboard bubbling. Preserve canonical printing from their focused button.
  const printShortcut = (event: KeyboardEvent) => {
    if (
      disposed ||
      viewer.documentId !== documentId ||
      event.defaultPrevented ||
      event.isComposing ||
      !(event.ctrlKey || event.metaKey) ||
      event.altKey ||
      event.shiftKey ||
      event.key.toLowerCase() !== "p"
    )
      return;
    const action = root.querySelector<HTMLButtonElement>(PDFJS_VIEWER_UI_HOOKS.actions.print);
    if (!action || action.disabled) return;
    event.preventDefault();
    if (event.repeat) return;
    closeDialog();
    action.click();
  };
  dialog.addEventListener("keydown", event => {
    printShortcut(event);
    event.stopPropagation();
  });
  close.addEventListener("click", closeDialog);
  dialog.addEventListener("cancel", event => {
    event.preventDefault();
    closeDialog();
  });
  // Backdrop clicks target the dialog; exclude blank space inside its border.
  let outside = false;
  const isOutside = (event: MouseEvent) => {
    const box = dialog.getBoundingClientRect();
    return (
      event.clientX < box.left ||
      event.clientX > box.right ||
      event.clientY < box.top ||
      event.clientY > box.bottom
    );
  };
  dialog.addEventListener("pointerdown", event => {
    outside = event.target === dialog && isOutside(event);
  });
  dialog.addEventListener("click", event => {
    event.stopPropagation();
    if (outside && event.target === dialog && isOutside(event)) closeDialog();
    outside = false;
  });
  cleanups.push(() => {
    closeDialog();
    dialog.remove();
  });
  const refreshers: (() => void)[] = [];
  let transactionFailed = false;
  const status = document.createElement("span");
  status.className = "sb-sr-only";
  status.setAttribute("role", "status");
  status.setAttribute("aria-live", "polite");
  root.append(status);
  cleanups.push(() => status.remove());
  refreshers.push(() => {
    const busy = controller.pending() || display.pending();
    status.textContent = busy
      ? translator.translateKey("pdf.transpose-pending")
      : transactionFailed || display.failed()
        ? translator.translateKey("pdf.transpose-error")
        : "";
  });
  const refresh = () => {
    if (viewer.documentId !== documentId) {
      dispose();
      return;
    }
    if (!disposed) for (const update of refreshers) update();
    if (!disposed && active) refreshDialog();
  };
  const relativeOffset = (value: number) => (value > 6 ? value - 12 : value);
  const offsetLabel = (value: number) => {
    const step = relativeOffset(value);
    return step > 0 ? `+${step}` : String(step);
  };
  const keyLabel = (record: MultikeyOccurrence, value: number) =>
    record.keyLabels?.[value] ??
    (value === 0
      ? (record.originalKey ?? translator.translateKey("pdf.transpose-original"))
      : translator.translateKey("pdf.transpose-offset", {
          values: { offset: offsetLabel(value) },
        }));
  const refreshDialog = () => {
    close.title = translator.translateKey("button.close");
    close.setAttribute("aria-label", close.title);
    if (!active) return;
    const { record } = active;
    dialog.setAttribute("aria-label", heading.textContent || active.button.title);
    for (const button of keys.querySelectorAll<HTMLButtonElement>("button")) {
      const offset = Number(button.dataset.offset);
      button.querySelector("span")!.textContent = keyLabel(record, offset);
      button.querySelector("small")!.textContent = offsetLabel(offset);
      button.setAttribute("aria-pressed", String(offset === active.offset()));
      button.title =
        offset === 0
          ? translator.translateKey("pdf.transpose-default", {
              values: { key: keyLabel(record, 0) },
            })
          : record.keyLabels?.[offset]
            ? `${keyLabel(record, offset)} (${translator.translateKey("pdf.transpose-offset", {
                values: { offset: offsetLabel(offset) },
              })})`
            : keyLabel(record, offset);
      button.setAttribute("aria-label", button.title);
    }
  };
  const documentChanged = (event: Event) => {
    if (viewer.documentId !== documentId) dispose();
    else {
      if ((event as CustomEvent<{ active?: boolean }>).detail?.active === false) closeDialog();
      refresh();
    }
  };
  root.addEventListener("pdf:statechange", documentChanged);
  cleanups.push(() => root.removeEventListener("pdf:statechange", documentChanged));
  const committed = (ok: boolean): void => {
    if (
      !ok &&
      !disposed &&
      viewer.documentId === documentId &&
      !controller.pending() &&
      !display.pending()
    ) {
      transactionFailed = true;
      Logger.warn("Multikey page transaction did not commit");
    } else if (ok) transactionFailed = false;
    refresh();
  };
  const failed = (error: unknown): void => {
    if (!disposed && viewer.documentId === documentId)
      Logger.warn("Multikey page transaction failed", error);
    transactionFailed = true;
    refresh();
  };
  cleanups.push(
    controller.subscribe(refresh),
    display.subscribe(refresh),
    translator.subscribe(refresh),
  );
  void controller.ready.then(ok => {
    if (!ok && !disposed && viewer.documentId === documentId && !controller.pending()) {
      transactionFailed = true;
      refresh();
    }
  }, failed);
  // Root removal is a fallback for consumers that replace markup before aborting.
  if (window && root.isConnected) {
    const observer = new window.MutationObserver(() => {
      if (!root.isConnected || (menu && !root.contains(menu))) dispose();
    });
    observer.observe(document, { childList: true, subtree: true });
    cleanups.push(() => observer.disconnect());
  }

  try {
    for (const record of controller.occurrences) {
      if (disposed) break;
      if (
        record.pageIndex === null ||
        !Number.isInteger(record.pageIndex) ||
        record.pageIndex < 0 ||
        !record.anchor ||
        !Number.isFinite(record.anchor.x) ||
        !Number.isFinite(record.anchor.y)
      )
        continue;
      const host = document.createElement("div");
      host.className = "sb-multikey-overlay";
      host.dataset.sbMultikeyPage = String(record.pageIndex + 1);
      const stack = document.createElement("div");
      stack.className = "sb-multikey-controls";
      stack.style.visibility = "hidden";
      host.append(stack);
      let displayedOffset = controller.offset(record);
      let songHeading = songTitle ? "" : record.songId;
      const button = document.createElement("button");
      button.type = "button";
      button.className = "sb-btn sb-btn--surface sb-btn--icon sb-multikey-button";
      button.setAttribute("aria-haspopup", "dialog");
      button.setAttribute("aria-expanded", "false");
      const glyph = document.createElement("i");
      glyph.className = "sbi-transpose";
      glyph.setAttribute("aria-hidden", "true");
      button.append(glyph);
      const click = (event: Event) => {
        event.stopPropagation();
        if (disposed || viewer.documentId !== documentId) return;
        closeDialog();
        const opened = { record, button, offset: () => displayedOffset };
        active = opened;
        // Do not flash the internal identity while the shared song index resolves.
        heading.textContent = songHeading;
        heading.setAttribute("aria-busy", String(!songHeading));
        // Higher keys are above lower keys; each column is independently centered.
        keys.replaceChildren();
        const columns = [1, 2, 3].map(() => {
          const column = document.createElement("div");
          column.className = "sb-transpose-key-column";
          keys.append(column);
          return column;
        });
        for (const step of [-1, -2, -3, -4, -5, 0, 6, 5, 4, 3, 2, 1]) {
          const offset = (step + 12) % 12;
          const choice = document.createElement("button");
          choice.type = "button";
          choice.className = "sb-btn sb-btn--surface";
          choice.dataset.offset = String(offset);
          if (offset === 0) choice.dataset.default = "true";
          const name = document.createElement("span");
          const hint = document.createElement("small");
          hint.setAttribute("aria-hidden", "true");
          choice.append(name, hint);
          choice.addEventListener("click", () => {
            if (
              disposed ||
              viewer.documentId !== documentId ||
              !dialog.open ||
              active?.record !== record
            )
              return;
            closeDialog();
            if (
              offset === displayedOffset &&
              offset !== 0 &&
              !controller.pending(record) &&
              !display.pending(record)
            )
              return;
            transactionFailed = false;
            void controller.select(record, offset).then(committed, failed);
          });
          columns[step < 0 ? 0 : step === 0 ? 1 : 2]!.append(choice);
        }
        refreshDialog();
        dialog.showModal();
        button.setAttribute("aria-expanded", "true");
        keys.querySelector<HTMLButtonElement>('[aria-pressed="true"]')?.focus();
        const updateTitle = (title: string) => {
          if (disposed || active !== opened) return;
          songHeading = title || record.songId;
          heading.textContent = songHeading;
          heading.setAttribute("aria-busy", "false");
          refreshDialog();
        };
        void songTitle?.(record).then(updateTitle, error => {
          Logger.debug("Song title unavailable", error);
          updateTitle(record.songId);
        });
      };
      button.addEventListener("click", click);
      button.addEventListener("keydown", printShortcut);
      cleanups.push(() => {
        button.removeEventListener("click", click);
        button.removeEventListener("keydown", printShortcut);
      });
      stack.append(button);
      refreshers.push(() => {
        const busy = controller.pending(record) || display.pending(record);
        const error = display.failed(record);
        if (!busy && !error) displayedOffset = controller.offset(record);
        stack.setAttribute("aria-busy", String(busy));
        stack.setAttribute("aria-invalid", String(error));
        stack.dataset.pending = String(busy);
        button.classList.toggle("sb-spinner", busy);
        glyph.hidden = busy;
        button.dataset.transposed = String(displayedOffset !== 0);
        const text = translator.translateKey("pdf.transpose", {
          values: { current: keyLabel(record, displayedOffset) },
        });
        button.title = text;
        button.setAttribute("aria-label", text);
      });
      let geometry: Readonly<PdfjsViewerPageOverlayGeometry> | undefined;
      let normalWidth = 0;
      let normalHeight = 0;
      const fit = () => {
        if (!geometry || disposed) return;
        const { x, y, width, height } = geometry;
        // Keep the control upright, but rotate its anchored corner with the page.
        const rotation = viewer.state.rotation;
        const left = rotation === 90 || rotation === 180;
        const up = rotation === 180 || rotation === 270;
        const scale = Math.min(
          1,
          (left ? x : width - x) / normalWidth,
          (up ? y : height - y) / normalHeight,
        );
        const visible =
          [x, y, width, height, normalWidth, normalHeight, scale].every(Number.isFinite) &&
          normalWidth > 0 &&
          normalHeight > 0 &&
          x >= 0 &&
          y >= 0 &&
          x <= width &&
          y <= height &&
          scale >= MIN_MULTIKEY_BUTTON_SCALE;
        stack.style.visibility = visible ? "inherit" : "hidden";
        stack.inert = !visible;
        stack.style.transform = `scale(${visible ? scale : 1})`;
        // The unscaled layout box must not enlarge the viewer's scrollable area.
        host.style.width = `${visible ? normalWidth * scale : 0}px`;
        host.style.height = `${visible ? normalHeight * scale : 0}px`;
        host.style.translate = `${visible && left ? -normalWidth * scale : 0}px ${visible && up ? -normalHeight * scale : 0}px`;
      };
      refreshers.push(fit);
      const result = await viewer.registerPageOverlay(
        {
          page: record.pageIndex + 1,
          anchor: record.anchor,
          element: host,
          onGeometry: next => {
            geometry = next;
            fit();
          },
        },
        documentId,
      );
      if (!result.ok) {
        host.remove();
        if (["stale-document", "destroyed", "not-ready"].includes(result.reason)) {
          dispose();
          break;
        }
        throw new Error(`Multikey page overlay registration failed: ${result.reason}`);
      }
      if (disposed || viewer.documentId !== documentId) {
        result.dispose();
        dispose();
        break;
      }
      cleanups.push(result.dispose);
      // Read once after attachment, before any transform. ResizeObserver reports unscaled sizes.
      const bounds = stack.getBoundingClientRect();
      normalWidth = bounds.width;
      normalHeight = bounds.height;
      fit();
      if (window?.ResizeObserver) {
        const observer = new window.ResizeObserver(entries => {
          const bounds = entries[0]?.contentRect;
          if (!bounds) return;
          normalWidth = bounds.width;
          normalHeight = bounds.height;
          fit();
        });
        observer.observe(stack);
        cleanups.push(() => observer.disconnect());
      }
    }
    if (!disposed && menu && controller.occurrences.length > 0) {
      menu.hidden = false;
      refreshers.push(() => {
        // Pending intent and failed pixels must remain resettable, even at committed offset zero.
        menu.disabled = !(
          controller.pending() ||
          display.pending() ||
          display.failed() ||
          controller.occurrences.some(record => controller.offset(record) !== 0)
        );
        menu.setAttribute("aria-disabled", String(menu.disabled));
        const text = translator.translateKey("pdf.reset-transpositions");
        menu.title = text;
        menu.setAttribute("aria-label", text);
        const label = menu.querySelector("span");
        if (label) label.textContent = text;
      });
      const reset = () => {
        if (disposed || menu.disabled || viewer.documentId !== documentId) return;
        root.querySelector<HTMLButtonElement>(PDFJS_VIEWER_UI_HOOKS.menu.close)?.click();
        transactionFailed = false;
        void controller.resetAll().then(committed, failed);
      };
      menu.addEventListener("click", reset);
      cleanups.push(() => menu.removeEventListener("click", reset));
    }
    refresh();
  } catch (error) {
    dispose();
    throw error;
  }
  return dispose;
}
